import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Button } from '@heroui/button';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import { Checkbox } from '@heroui/checkbox';

import { Songbook } from '@/core/db.ts';
import { useSongs } from '@/hooks/useSongbook.ts';
import { songName } from '@/core/song.ts';

interface SongbookTreeItemProps {
  songbook: Songbook;
  defaultSong?: string;
  isSelected?: boolean;
  selectedSongs?: string[];
  setSelectedSongs: (songs: string[]) => void;
}

function SongbookTreeItem({
  songbook,
  defaultSong,
  isSelected = false,
  selectedSongs = [],
  setSelectedSongs,
}: SongbookTreeItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { isLoading, data } = useSongs(songbook.id);

  const onToggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const onChangeSongToggle = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const songId = e.target.value;
    const isOn = e.target.checked;

    const withoutSong = selectedSongs.filter((s) => s !== songId);

    if (isOn) {
      setSelectedSongs([...withoutSong, songId]);
    } else {
      setSelectedSongs(withoutSong);
    }
  }, [selectedSongs, setSelectedSongs]);

  useEffect(
    () => {
      if (isSelected && data) {
        setSelectedSongs(data.map((s) => s.id));
      } else if (defaultSong && data && data.find((s) => s.id === defaultSong)) {
        setSelectedSongs([defaultSong]);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsOpen(true);
      }
    },
    [data, isSelected, setSelectedSongs],
  );

  const isAllSelected = selectedSongs.length === data?.length;
  const isPartiallySelected = !isAllSelected && selectedSongs.length > 0;

  const onToggleSongbook = useCallback(() => {
    if (isAllSelected || isPartiallySelected) {
      setSelectedSongs([]);
    } else if (data) {
      setSelectedSongs(data.map((s) => s.id));
    }
  }, [isAllSelected, isPartiallySelected, data, setSelectedSongs]);

  if (isLoading || !data) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center">
        <Button isIconOnly className="mr-1" variant="light" onPress={onToggleOpen}>
          {isOpen ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}
        </Button>
        <Checkbox
          isIndeterminate={isPartiallySelected}
          isSelected={isAllSelected}
          onChange={onToggleSongbook}
        >
          {songbook.title}
        </Checkbox>
      </div>
      {isOpen && data && (
        <div className="flex flex-col ml-14">
          {data.map((song) => (
            <div key={song.id} className="h-8 flex items-center">
              <Checkbox
                className="truncate"
                classNames={{ label: 'truncate' }}
                isSelected={selectedSongs.includes(song.id)}
                value={song.id}
                onChange={onChangeSongToggle}
              >
                {songName(song)}
              </Checkbox>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SongbookTreeItem;
